import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  useMediaQuery,
  Autocomplete,
} from "@mui/material";
import { useForm, FormProvider, Controller } from "react-hook-form";
import DateAndTime from "../../components/DateAndTime";
import { formatLocalDateTime } from "../../components/getDateValues";
import { useEditTaskMutation } from "../../redux/api/tasksApi";
import { useSelector } from "react-redux";
import { selectCurrentagency } from "../../redux/features/authSlice";
import { useGetAgentsQuery } from "../../redux/api/agentsApi";
import { TASK_STATUSES } from "../../constants/task";

const parseLocalDateTime = (dateTimeString) => {
  const dateObj = new Date(dateTimeString);
  const dateOnly = new Date(
    dateObj.getFullYear(),
    dateObj.getMonth(),
    dateObj.getDate()
  );
  const timeOnly = new Date();
  timeOnly.setHours(dateObj.getHours(), dateObj.getMinutes(), 0, 0);
  return { date: dateOnly, time: timeOnly };
};

export default function EditTask({ task, onClose }) {
  const currentAgency = useSelector(selectCurrentagency);
  const [editTask, { isLoading }] = useEditTaskMutation();
  const methods = useForm();
  const { handleSubmit, control, setValue } = methods;

  const [startDate, setStartDate] = useState(null);
  const [startTime, setStartTime] = useState(null);

  const isLargeScreen = useMediaQuery("(min-width:1024px)");
  const { data: agents } = useGetAgentsQuery(currentAgency.agencyid);

  useEffect(() => {
    if (task) {
      const matchedStatus = TASK_STATUSES.find((s) => s.value === task.status);
      setValue("status", matchedStatus || null);
      setValue("agentid", task.receiverAgentId);
      setValue("description", task.description);

      const parsed = parseLocalDateTime(task.deadLine);
      setStartDate(parsed.date);
      setStartTime(parsed.time);
    }
  }, [task, setValue]);

  const onSubmit = async (data) => {
    if (!startDate || !startTime) {
      alert("Please select both a deadline date and time.");
      return;
    }

    const payload = {
      taskId: task.taskId,
      receiverAgent: data.agentid,
      description: data.description,
      taskReceivedDate: task.taskReceivedDate,
      status: data.status?.value,
      agencyId: currentAgency.agencyid,
      deadLine: formatLocalDateTime(startDate, startTime),
    };

    try {
      await editTask(payload).unwrap();
      onClose();
    } catch (err) {
      console.error("Failed to update task:", err);
    }
  };

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ p: 4, maxWidth: isLargeScreen ? 500 : "100%", mx: "auto" }}
      >
        <Typography variant="h5" mb={2}>
          Edit Task
        </Typography>

        {/* Agent Field */}
        <Controller
          name="agentid"
          control={control}
          rules={{ required: "Agent is required" }}
          render={({ field }) => (
            <Autocomplete
              value={agents?.find((a) => a.agentid === field.value) || null}
              onChange={(e, newValue) =>
                field.onChange(newValue ? newValue.agentid : null)
              }
              options={agents || []}
              getOptionLabel={(option) => option.agentname || ""}
              isOptionEqualToValue={(option, value) =>
                option.agentid === value.agentid
              }
              renderInput={(params) => (
                <TextField {...params} label="Select Staff" margin="normal" />
              )}
              size={"small"}
              fullWidth
            />
          )}
        />

        {/* Status Field */}
        <Controller
          name="status"
          control={control}
          rules={{ required: "Status is required" }}
          render={({ field }) => (
            <Autocomplete
              value={field.value || null}
              onChange={(e, newValue) => field.onChange(newValue)}
              options={TASK_STATUSES}
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) =>
                option.value === value?.value
              }
              renderInput={(params) => (
                <TextField {...params} label="Select Status" margin="normal" />
              )}
              fullWidth
              size={"small"}
            />
          )}
        />

        {/* Description */}
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="Task Description"
              multiline
              rows={3}
              margin="normal"
            />
          )}
        />

        {/* Date and Time */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" mb={1}>
            Deadline Date
          </Typography>
          <Box display="flex" gap={1} mb={2}>
            <DateAndTime
              startDate={startDate}
              setStartDate={setStartDate}
              startTime={startTime}
              setStartTime={setStartTime}
            />
          </Box>
        </Box>

        <Box display="flex" justifyContent="space-between" width="100%" gap={2}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{ color: "white" }}
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Box>
    </FormProvider>
  );
}
